import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import * as colors from 'Styles/colors';

import Block from '../Block/Block';
import RichTextPropTypes from '../../prop-types/rich-text';
import List from '../List/List';

const EmbeddedVideo = lazy(() => import('../EmbeddedVideo/EmbeddedVideo'));

/**
 * Converts contentful rich text data into react components
 */
const ContentfulTextBlock = ({
  customBackgroundColor,
  backgroundColor,
  content,
  bulletType,
  columnType,
  ...props
}) => {
  const bgColor = customBackgroundColor || colors[backgroundColor];
  const richTextOptions = {
    renderNode: {
      [BLOCKS.UL_LIST]: (node, children) => (
        <List
          $bulletType={bulletType}
          $columnType={columnType}
          $itemSpacing={4}
          $listItems={React.Children.count(children)}
        >
          {children}
        </List>
      ),
      [BLOCKS.OL_LIST]: (node, children) => (
        <List
          $columnType={columnType}
          $itemSpacing={4}
          $listItems={React.Children.count(children)}
          as="ol"
        >
          {children}
        </List>
      ),
      [BLOCKS.PARAGRAPH]: (node, children) =>
        children[0] === '' && children.length === 1 ? <></> : <p>{children}</p>,
      [INLINES.EMBEDDED_ENTRY]: (node) => {
        if (!node.data.target.fields) return null;

        const { id, title, name } = node.data.target.fields;

        return (
          <Suspense fallback={null}>
            <EmbeddedVideo id={id} title={title} name={name} />
          </Suspense>
        );
      }
    }
  };

  return (
    <Block backgroundColor={bgColor} __skipValidate {...props}>
      {content &&
        Object.keys(content).length &&
        documentToReactComponents(content, richTextOptions)}
    </Block>
  );
};

ContentfulTextBlock.propTypes = {
  /**
   * Rich text content
   */
  content: PropTypes.shape(RichTextPropTypes),
  /**
   * The NAME of the color passed from the API
   *
   * @example white, grey20, bombasBlue
   *
   * The name passed from the API will be mapped to the color name to retrieve a hex code
   */
  backgroundColor: PropTypes.oneOf(Object.keys(colors)),
  /**
   * Type of prefix to a list item
   */
  bulletType: PropTypes.oneOf(['default bullet', 'checkmark']),
  /**
   * Column type
   */
  columnType: PropTypes.oneOf(['one column', 'two column']),
  /**
   * A prop for off-brand colors.
   *
   * If this field is present, it will override 'backgroundColor'
   */
  customBackgroundColor: PropTypes.string
};

ContentfulTextBlock.defaultProps = {
  backgroundColor: 'white',
  bulletType: 'checkmark',
  columnType: 'two column',
  content: '',
  customBackgroundColor: null
};

export default ContentfulTextBlock;
